import React, { useState, useMemo, useEffect } from "react";
import {
  Box, Typography, Paper, Grid, Card, CardContent, Tabs, Tab,
  TextField, InputAdornment, IconButton, Button, Chip, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Checkbox, Tooltip, CircularProgress, MenuItem, Select,
  FormControl, InputLabel,
} from "@mui/material";
import {
  Search, Refresh, Delete, Print, GetApp, TrendingUp, TrendingDown,
  AccessTime, CheckCircle, Pending, ArrowForward, Sell, Autorenew, Visibility
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import { useNotification } from "../../context/notification.context";
import { product } from "../../api/paquetes";
import * as XLSX from 'xlsx';

// Registrar componentes de Chart.js
ChartJS.register(ArcElement, ChartTooltip, Legend, CategoryScale, LinearScale, BarElement);

// Interfaces
interface PackageData {
  id: number;
  numero: number;
  estado: string;
  fechaVenta: Date;
  fechaSalida?: Date;
  vendedor: string;
  cliente: string;
  concepto: string;
  tarifa: number;
  costos: number;
  ganancia: number;
  saldo: number;
  moneda: string;
  pasajeros: number;
  pendientePago?: boolean;
  revisado?: boolean;
  listoParaFinalizar?: boolean;
}

interface MetricCard {
  title: string;
  value: number;
  change: number;
  changeType: 'increase' | 'decrease' | 'neutral';
  color: string;
  icon: React.ReactNode;
  subLabel?: string;
}

const Dashboard: React.FC = () => {
  // Estados principales
  const [paquetes, setPaquetes] = useState<PackageData[]>([]);
  const [pestañaSeleccionada, setPestañaSeleccionada] = useState(0);
  const [terminoBusqueda, setTerminoBusqueda] = useState("");
  const [pagina, setPagina] = useState(0);
  const [filasPorPagina, setFilasPorPagina] = useState(20);
  const [seleccionados, setSeleccionados] = useState<number[]>([]);
  const [campoOrdenamiento, setCampoOrdenamiento] = useState<keyof PackageData>("fechaVenta");
  const [direccionOrdenamiento, setDireccionOrdenamiento] = useState<"asc" | "desc">("desc");
  const [cargando, setCargando] = useState(true);
  const navigate = useNavigate();
  const { getSuccess, getError } = useNotification();

  // --- RBAC: determinar si el usuario es admin ---
  const userData = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}"); }
    catch { return {}; }
  }, []);
  const isAdmin = userData.rol === "Superadministrador" || userData.rol === "Administrador";

  // --- Analytics: estados para el módulo nuevo ---
  const ahora = new Date();
  const [analyticsMes, setAnalyticsMes] = useState(ahora.getMonth() + 1);
  const [analyticsAnio, setAnalyticsAnio] = useState(ahora.getFullYear());
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [analyticsCargando, setAnalyticsCargando] = useState(false);

  // Cargar analytics cuando cambia el mes/año seleccionado
  useEffect(() => {
    const cargarAnalytics = async () => {
      try {
        setAnalyticsCargando(true);
        const vendedor = !isAdmin ? (userData.nombre || "").trim() : undefined;
        const response = await product.getAnalytics(analyticsMes, analyticsAnio, vendedor);
        setAnalyticsData(response.data);
      } catch (error) {
        // Silencioso: si falla analytics, el resto del dashboard sigue funcionando
      } finally {
        setAnalyticsCargando(false);
      }
    };
    cargarAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticsMes, analyticsAnio]);

  // Cargar datos desde la API
  const cargarPaquetes = async () => {
    try {
      setCargando(true);
      const response = await product.getAll();
      const payload = response.data;
      const paquetesAPI = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);

      // Filtrar solo paquetes individuales (excluir grupales y pruebas)
      const paquetesIndividuales = paquetesAPI.filter((p: any) => {
        const descripcion = p.descripcion || '';
        const concepto = (p.concepto || '').toLowerCase();
        // Excluir grupales y paquetes de prueba
        if (descripcion.includes('[GRUPAL:')) return false;
        if (concepto.includes('prueba')) return false;
        return true;
      });

      // Transformar datos de la API al formato del Dashboard
      const paquetesTransformados: PackageData[] = paquetesIndividuales.map((p: any) => {
        // Calcular costos sumando importe_costo de todos los servicios
        const aereos = p.aereosPaquetes || p.aereos || [];
        const hoteles = p.hotelesPaquetes || p.hoteles || [];
        const extras = p.serviciosPaquetes || p.extras || [];

        const costoAereos = aereos.reduce((sum: number, s: any) => sum + (parseFloat(s.importe_costo) || 0), 0);
        const costoHoteles = hoteles.reduce((sum: number, s: any) => sum + (parseFloat(s.importe_costo) || 0), 0);
        const costoExtras = extras.reduce((sum: number, s: any) => sum + (parseFloat(s.importe_costo) || 0), 0);
        const totalCostos = costoAereos + costoHoteles + costoExtras;

        const tarifa = parseFloat(p.importe) || 0;
        const ganancia = tarifa - totalCostos;

        // Calcular pagos realizados para obtener saldo
        // Calcular pagos realizados para obtener saldo, considerando monedas y tipo de cambio
        const pagos = p.pagosPaquetes || [];
        const monedaPaq = (p.moneda || 'USD').toUpperCase();

        const totalPagado = pagos
          .filter((pago: any) => !pago.deleted_at)
          .reduce((acc: number, pago: any) => {
            const importe = parseFloat(pago.importe) || 0;
            const monedaPago = (pago.moneda || 'USD').toUpperCase();

            // Si la moneda del pago coincide con la del paquete, sumar directamente
            if (monedaPago === monedaPaq) {
              return acc + importe;
            }

            // Si el backend ya trajo un monto convertido, usarlo (cubrir camelCase y snake_case)
            const montoConv = parseFloat(pago.montoConvertido ?? pago.monto_convertido ?? pago.montoUSD ?? pago.monto_usd ?? NaN);
            if (!Number.isNaN(montoConv) && montoConv > 0) {
              return acc + montoConv;
            }

            // Si hay tipo de cambio, calcular la conversión hacia la moneda del paquete
            const tc = parseFloat(pago.tipoCambio ?? pago.tipo_cambio ?? pago.tc ?? 0);
            if (tc > 0) {
              if (monedaPaq === 'USD' && monedaPago === 'ARS') {
                return acc + (importe / tc);
              }
              if (monedaPaq === 'ARS' && monedaPago === 'USD') {
                return acc + (importe * tc);
              }
            }

            // Si no hay forma de convertir, ignorar el pago
            return acc;
          }, 0);

        const saldo = tarifa - totalPagado;

        // Determinar si el paquete está listo para finalizar
        // Requisitos: saldo=0, todos los servicios revisados (precio y costo), 
        // todos los pagos revisados, fecha de salida pasada
        const fechaSalida = p.fecha_salida ? new Date(p.fecha_salida) : null;
        const hoy = new Date();
        const fechaSalidaPasada = fechaSalida ? fechaSalida < hoy : false;

        // Verificar que todos los servicios tengan revisado y revisado_costo = true
        // NOTA: Usamos Boolean() porque MySQL tinyint devuelve 1/0, no true/false
        const todosServicios = [...aereos, ...hoteles, ...extras];
        const serviciosExisten = todosServicios.length > 0;
        const todosServiciosRevisados = !serviciosExisten || todosServicios.every(
          (s: any) => Boolean(s.revisado) && Boolean(s.revisado_costo)
        );

        // Verificar que todos los pagos tengan revisado_por_nombre asignado
        const pagosActivos = pagos.filter((pago: any) => !pago.deleted_at);
        const pagosExisten = pagosActivos.length > 0;
        const todosPagosRevisados = !pagosExisten || pagosActivos.every(
          (pago: any) => Boolean(pago.revisado_por_nombre)
        );

        const listoParaFinalizar =
          Math.abs(saldo) < 0.1 &&
          todosServiciosRevisados &&
          todosPagosRevisados &&
          fechaSalidaPasada;

        return {
          id: p.id,
          numero: p.id,
          estado: p.estado || "Vendido",
          fechaVenta: p.created_at ? new Date(p.created_at) : new Date(),
          fechaSalida: fechaSalida || undefined,
          vendedor: p.vendedor_nombre || p.vendedor || "Sistema",
          cliente: [p.cliente?.nombre, p.cliente?.apellido].filter(Boolean).join(' ') || "Sin cliente",
          concepto: p.concepto || "Sin concepto",
          tarifa: tarifa,
          costos: totalCostos,
          ganancia: ganancia,
          saldo: saldo,
          moneda: p.moneda || "USD",
          pasajeros: (p.adultos || 0) + (p.ninos || 0) + (p.infantes || 0),
          pendientePago: Boolean(p.pendiente_pago),
          revisado: false,
          listoParaFinalizar: listoParaFinalizar,
        };
      });

      // RBAC: si no es admin, mostrar solo los paquetes del vendedor actual
      if (!isAdmin) {
        const miNombre = (userData.nombre || "").trim().toLowerCase();
        const miId = Number(userData.id || 0);
        const misPaquetes = paquetesTransformados.filter(p => {
          const vendedorNombre = (p.vendedor || "").trim().toLowerCase();
          return vendedorNombre === miNombre || (miId > 0 && (p as any).usuario_id === miId);
        });
        setPaquetes(misPaquetes);
      } else {
        setPaquetes(paquetesTransformados);
      }
    } catch (error) {
      getError("Error al cargar los paquetes");
    } finally {
      setCargando(false);
    }
  };

  // Cargar datos al montar el componente
  useEffect(() => {
    cargarPaquetes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Configuración de estados de las tablas
  const estadosTabla = useMemo(() => ["Pendientes", "Enviados", "Vendidos", "Revisión Operativa", "Revisados"], []);
  const mapeoEstados = useMemo(() => ({
    "Pendientes": "Pendiente",
    "Enviados": "Enviado",
    "Vendidos": "Vendido",
    "Revisión Operativa": "Revisión Operativa",
    "Revisados": "Revisado"
  }), []);

  // Genera las métricas del dashboard - optimizado con useMemo
  const obtenerMetricas = useMemo((): MetricCard[] => {
    const totalPaquetes = paquetes.length;
    const ingresosTotales = paquetes.reduce((sum, pkg) => sum + pkg.tarifa, 0);
    const paquetesPendientes = paquetes.filter(pkg => pkg.estado === "Pendiente").length;
    const paquetesVendidos = paquetes.filter(pkg => pkg.estado === "Vendido").length;

    return [
      {
        title: "Pendientes",
        value: paquetesPendientes,
        change: 12.5,
        changeType: 'increase',
        color: 'linear-gradient(135deg,rgb(251, 159, 159),rgb(236, 106, 99), #e74c3c)',
        icon: <Pending />,
        subLabel: `${((paquetesPendientes / totalPaquetes) * 100).toFixed(1)}% del total`
      },
      {
        title: "Enviados",
        value: paquetes.filter(pkg => pkg.estado === "Enviado").length,
        change: -5.2,
        changeType: 'decrease',
        color: 'linear-gradient(135deg, #4facfe,rgb(0, 202, 213), #3498db)',
        icon: <ArrowForward />,
        subLabel: "En proceso"
      },
      {
        title: "Vendidos",
        value: paquetesVendidos,
        change: 8.3,
        changeType: 'increase',
        color: 'linear-gradient(135deg,rgb(146, 227, 211), #00bfa5, #26a69a)',
        icon: <Sell />,
        subLabel: `$${ingresosTotales.toLocaleString()}`
      },
      {
        title: "Revisión Operativa",
        value: paquetes.filter(pkg => pkg.estado === "Revisión Operativa").length,
        change: 2.1,
        changeType: 'increase',
        color: 'linear-gradient(135deg, rgb(255, 182, 193), rgb(255, 130, 171), rgb(219, 112, 147))',
        icon: <AccessTime />,
        subLabel: "En revisión"
      },
      {
        title: "Revisados",
        value: paquetes.filter(pkg => pkg.estado === "Revisado").length,
        change: 0,
        changeType: 'neutral',
        color: 'linear-gradient(135deg, #667eea, #764ba2, #9b59b6)',
        icon: <CheckCircle />,
        subLabel: "A finalizar"
      },
    ];
  }, [paquetes]);

  // Genera datos para el resumen de actividad de los últimos 7 días - optimizado
  const obtenerDatosGrafico = useMemo(() => {
    const ultimos7Dias = Array.from({ length: 7 }, (_, i) => {
      const fecha = new Date();
      fecha.setDate(fecha.getDate() - i);
      return fecha;
    });

    return ultimos7Dias.map(fecha => {
      const paquetesDelDia = paquetes.filter(pkg =>
        pkg.fechaVenta.toDateString() === fecha.toDateString()
      );

      return {
        fecha: fecha.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' }),
        vendidos: paquetesDelDia.filter(pkg => pkg.estado === "Vendido").length,
        pendientes: paquetesDelDia.filter(pkg => pkg.estado === "Pendiente").length,
        ingresosARS: paquetesDelDia.filter(pkg => pkg.moneda === "ARS").reduce((sum, pkg) => sum + pkg.tarifa, 0),
        ingresosUSD: paquetesDelDia.filter(pkg => pkg.moneda === "USD").reduce((sum, pkg) => sum + pkg.tarifa, 0)
      };
    });
  }, [paquetes]);

  // Handlers para selección
  const handleSelectRow = (id: number) => {
    setSeleccionados(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSeleccionados(paquetesFiltrados.map(p => p.id));
    } else {
      setSeleccionados([]);
    }
  };

  const handleBulkStateChange = async () => {
    if (seleccionados.length === 0) return;

    try {
      setCargando(true);
      // Agrupar IDs por el estado al que deben pasar
      const gruposPorEstado: Record<string, number[]> = {};

      seleccionados.forEach(id => {
        const pkg = paquetes.find(p => p.id === id);
        if (pkg && pkg.estado !== "Finalizado") {
          const { nextState } = obtenerInfoSiguienteEstado(pkg.estado);
          if (nextState) {
            if (!gruposPorEstado[nextState]) gruposPorEstado[nextState] = [];
            gruposPorEstado[nextState].push(id);
          }
        }
      });

      let exitos = 0;
      let errores = 0;

      // Realizar una llamada masiva por cada grupo de estado
      for (const [nuevoEstado, ids] of Object.entries(gruposPorEstado)) {
        try {
          await product.bulkUpdateEstado(ids, nuevoEstado);
          exitos += ids.length;
        } catch (e) {
          errores += ids.length;
        }
      }

      if (exitos > 0) getSuccess(`Se actualizaron ${exitos} paquetes correctamente.`);
      if (errores > 0) getError(`Error al actualizar ${errores} paquetes.`);

      setSeleccionados([]);
      await cargarPaquetes();
    } catch (error) {
      getError("Error en la operación masiva");
    } finally {
      setCargando(false);
    }
  };

  // Genera datos para el gráfico de dona - optimizado
  const obtenerDistribucionEstados = useMemo(() => {
    const estados = ["Pendiente", "Enviado", "Vendido", "Revisión Operativa", "Revisado"];
    const colores = ['#ff6b6b', '#4facfe', '#26a69a', '#ff82ab', '#764ba2'];

    const datos = estados.map(estado => paquetes.filter(pkg => pkg.estado === estado).length);

    return {
      labels: estados,
      datasets: [{
        data: datos,
        backgroundColor: colores,
        borderColor: colores.map(color => color + '80'),
        borderWidth: 2,
        hoverOffset: 0,
        cutout: '60%'
      }],
      // Datos adicionales para la leyenda
      rawData: estados.map((estado, index) => ({
        name: estado,
        value: datos[index],
        color: colores[index]
      }))
    };
  }, [paquetes]);

  // Filtros y búsqueda - optimizado con useMemo
  const paquetesFiltrados = useMemo(() => {
    let filtrados = paquetes;

    // Filtrar por estado de tabla seleccionada
    const estadoActual = mapeoEstados[estadosTabla[pestañaSeleccionada] as keyof typeof mapeoEstados];
    if (estadoActual) {
      filtrados = filtrados.filter(pkg => pkg.estado === estadoActual);
    }

    // Filtrar por término de búsqueda
    if (terminoBusqueda.trim()) {
      const terminoLower = terminoBusqueda.toLowerCase();
      filtrados = filtrados.filter(pkg =>
        pkg.cliente.toLowerCase().includes(terminoLower) ||
        pkg.concepto.toLowerCase().includes(terminoLower) ||
        pkg.vendedor.toLowerCase().includes(terminoLower) ||
        pkg.numero.toString().includes(terminoBusqueda)
      );
    }

    // Ordenar
    filtrados.sort((a, b) => {
      const valorA = a[campoOrdenamiento];
      const valorB = b[campoOrdenamiento];

      if (valorA === undefined || valorB === undefined) return 0;
      if (valorA === null || valorB === null) return 0;
      if (valorA < valorB) return direccionOrdenamiento === "asc" ? -1 : 1;
      if (valorA > valorB) return direccionOrdenamiento === "asc" ? 1 : -1;
      return 0;
    });

    return filtrados;
  }, [paquetes, pestañaSeleccionada, terminoBusqueda, campoOrdenamiento, direccionOrdenamiento, mapeoEstados, estadosTabla]);

  // Handlers para interacciones
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setPestañaSeleccionada(newValue);
    setPagina(0);
  };

  const handleSort = (field: keyof PackageData) => {
    if (campoOrdenamiento === field) {
      setDireccionOrdenamiento(direccionOrdenamiento === "asc" ? "desc" : "asc");
    } else {
      setCampoOrdenamiento(field);
      setDireccionOrdenamiento("asc");
    }
  };

  const handleRefresh = async () => {
    await cargarPaquetes();
    getSuccess("Datos actualizados correctamente");
  };

  const handleExport = () => {
    const nombresEstados = ["Pendientes", "Enviados", "Vendidos", "Revisión Operativa", "Revisados"];
    const estadoActual = nombresEstados[pestañaSeleccionada];

    // Preparar datos para Excel
    const datos = paquetesFiltrados.map(pkg => ({
      '#': pkg.numero,
      'Fecha Venta': pkg.fechaVenta.toLocaleDateString('es-ES'),
      'Vendedor': pkg.vendedor,
      'Cliente': pkg.cliente,
      'Concepto': pkg.concepto,
      'Moneda': pkg.moneda,
      'Tarifa': pkg.tarifa,
      'Costos': pkg.costos,
      'Ganancia': pkg.ganancia,
      'Saldo': pkg.saldo,
    }));

    // Crear workbook y worksheet
    const ws = XLSX.utils.json_to_sheet(datos);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Paquetes');

    // Descargar archivo
    const nombreArchivo = 'paquetes_' + estadoActual.replace(/ /g, '_') + '_' + new Date().toISOString().split('T')[0] + '.xlsx';
    XLSX.writeFile(wb, nombreArchivo);
    getSuccess('Excel exportado: ' + nombreArchivo);
  };

  const handlePrint = () => {
    const nombresEstados = ["Pendientes", "Enviados", "Vendidos", "Revisión Operativa", "Revisados"];
    const estadoActual = nombresEstados[pestañaSeleccionada];
    const html = '<!DOCTYPE html><html><head><title>Lista de Paquetes - ' + estadoActual + '</title>' +
      '<style>' +
      'body { font-family: Arial, sans-serif; padding: 20px; }' +
      '.logo { width: 120px; margin-bottom: 10px; }' +
      'h2 { margin: 0 0 5px 0; color: #333; }' +
      '.info { font-size: 12px; color: #666; margin-bottom: 15px; }' +
      'table { width: 100%; border-collapse: collapse; font-size: 11px; }' +
      'th, td { border: 1px solid #ccc; padding: 6px; text-align: left; }' +
      'th { background: #7c3aed; color: white; }' +
      '</style></head><body>' +
      '<img src="/logo.png" class="logo" />' +
      '<h2>Lista de Paquetes - ' + estadoActual + '</h2>' +
      '<div class="info">Fecha: ' + new Date().toLocaleDateString('es-ES') + ' | Total: ' + paquetesFiltrados.length + ' paquetes</div>' +
      '<table><thead><tr><th>#</th><th>Fecha</th><th>Vendedor</th><th>Cliente</th><th>Concepto</th><th>Tarifa</th><th>Costos</th><th>Ganancia</th><th>Saldo</th></tr></thead><tbody>' +
      paquetesFiltrados.map(pkg =>
        '<tr><td>' + pkg.numero + '</td><td>' + pkg.fechaVenta.toLocaleDateString('es-ES') + '</td><td>' + pkg.vendedor + '</td><td>' + pkg.cliente + '</td><td>' + pkg.concepto + '</td><td>' + formatearMoneda(pkg.tarifa, pkg.moneda) + '</td><td>' + formatearMoneda(pkg.costos, pkg.moneda) + '</td><td>' + formatearMoneda(pkg.ganancia, pkg.moneda) + '</td><td>' + formatearMoneda(pkg.saldo, pkg.moneda) + '</td></tr>'
      ).join('') +
      '</tbody></table></body></html>';

    const ventana = window.open('', '_blank');
    if (ventana) {
      ventana.document.write(html);
      ventana.document.close();
      ventana.onload = function () { ventana.print(); };
    }
  };

  // Función para cambiar el estado de un paquete
  const handleStateChange = async (packageId: number, nuevoEstadoParam?: string) => {
    const packageIndex = paquetes.findIndex(pkg => pkg.id === packageId);
    if (packageIndex === -1) return;

    const paqueteActual = paquetes[packageIndex];

    // Si está en "Finalizado", no cambia de estado (es final)
    if (paqueteActual.estado === "Finalizado" && !nuevoEstadoParam) {
      getSuccess(`Paquete #${paqueteActual.numero} ya está finalizado`);
      return;
    }

    // Determinar nuevo estado
    let nuevoEstado: string;
    if (nuevoEstadoParam) {
      nuevoEstado = nuevoEstadoParam;
    } else {
      const secuenciaEstados = ["Pendiente", "Enviado", "Vendido", "Revisión Operativa", "Revisado", "Finalizado"];
      const indiceActual = secuenciaEstados.indexOf(paqueteActual.estado);
      if (indiceActual === -1 || indiceActual >= secuenciaEstados.length - 1) {
        return;
      }
      nuevoEstado = secuenciaEstados[indiceActual + 1];
    }

    try {
      // Llamar a la API para persistir el cambio
      await product.updateEstado(packageId, nuevoEstado);

      // Actualizar el estado local
      const paquetesActualizados = [...paquetes];
      paquetesActualizados[packageIndex] = {
        ...paqueteActual,
        estado: nuevoEstado
      };
      setPaquetes(paquetesActualizados);
      getSuccess(`Paquete #${paqueteActual.numero} cambiado a ${nuevoEstado}`);
    } catch (error) {
      getError("Error al cambiar el estado del paquete");
    }
  };

  // Función para marcar como pendiente de pago
  const handlePaymentPendingToggle = async (packageId: number) => {
    const packageIndex = paquetes.findIndex(pkg => pkg.id === packageId);
    if (packageIndex === -1) return;

    const nuevoValor = !paquetes[packageIndex].pendientePago;

    try {
      // Llamar a la API para persistir el cambio
      await product.updatePendientePago(packageId, nuevoValor);

      // Actualizar estado local
      const paquetesActualizados = [...paquetes];
      paquetesActualizados[packageIndex] = {
        ...paquetesActualizados[packageIndex],
        pendientePago: nuevoValor
      };
      setPaquetes(paquetesActualizados);
      getSuccess(`Pendiente de pago ${nuevoValor ? 'activado' : 'desactivado'}`);
    } catch (error) {
      getError('Error al actualizar pendiente de pago');
    }
  };

  // Función para obtener el próximo estado y el texto del tooltip
  const obtenerInfoSiguienteEstado = (estadoActual: string) => {
    const secuenciaEstados = ["Pendiente", "Enviado", "Vendido", "Revisión Operativa", "Revisado", "Finalizado"];
    const indiceActual = secuenciaEstados.indexOf(estadoActual);

    // Si está en "Finalizado", no hay siguiente estado
    if (estadoActual === "Finalizado") {
      return {
        nextState: null,
        tooltipText: "Paquete finalizado - No se puede cambiar de estado"
      };
    }

    const siguienteIndice = indiceActual + 1;
    const siguienteEstado = secuenciaEstados[siguienteIndice];

    return {
      nextState: siguienteEstado,
      tooltipText: `Cambiar de ${estadoActual} a ${siguienteEstado}`
    };
  };

  // Utilidad para formatear moneda
  const formatearMoneda = (amount: number, currency: string) => {
    if (amount === 0) return "-";
    return `${currency === "USD" ? "U$D" : "$"} ${amount.toLocaleString()}`;
  };

  const metricas = obtenerMetricas;
  const datosGrafico = obtenerDatosGrafico;
  const distribucionEstados = obtenerDistribucionEstados;

  return (
    <Box sx={{ mt: 3 }}>
      {/* Título del Dashboard */}
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
        Dashboard: Paquetes
      </Typography>

      {/* Tarjetas de Métricas */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {metricas.map((metrica, index) => (
          <Grid item xs={12} sm={6} md={2.4} key={index}>
            <Card
              sx={{
                height: '100%',
                background: metrica.color,
                color: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)'
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  {metrica.icon}
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {metrica.value}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, opacity: 0.9 }}>
                  {metrica.title}
                </Typography>
                {metrica.subLabel && (
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {metrica.subLabel}
                  </Typography>
                )}
                {metrica.change !== 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                    {metrica.changeType === 'increase' ?
                      <TrendingUp fontSize="small" /> :
                      <TrendingDown fontSize="small" />
                    }
                    <Typography variant="caption" sx={{ ml: 0.5 }}>
                      {Math.abs(metrica.change)}%
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ============= MÓDULO DE ANALYTICS ============= */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Selector de Mes/Año */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mr: 2 }}>
              {isAdmin ? 'Analytics' : 'Mi Resumen'}
            </Typography>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Mes</InputLabel>
              <Select
                value={analyticsMes}
                label="Mes"
                onChange={(e) => setAnalyticsMes(Number(e.target.value))}
              >
                {['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'].map((nombre, i) => (
                  <MenuItem key={i + 1} value={i + 1}>{nombre}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Año</InputLabel>
              <Select
                value={analyticsAnio}
                label="Año"
                onChange={(e) => setAnalyticsAnio(Number(e.target.value))}
              >
                {[2026, 2027, 2028].map(y => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {analyticsCargando && <CircularProgress size={20} />}
          </Paper>
        </Grid>

        {/* Paquetes Creados */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                {isAdmin ? 'Paquetes Creados' : 'Mis Paquetes Creados'}
              </Typography>
              <Chip
                label={`Total: ${analyticsData?.paquetes?.total ?? 0}`}
                color="primary"
                sx={{ fontWeight: 700, fontSize: '0.9rem' }}
              />
            </Box>
            {analyticsCargando ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
              <Box sx={{ height: 220 }}>
                <Bar
                  data={{
                    labels: [1, 2, 3, 4, 5].map(s => `Semana ${s}`),
                    datasets: [{
                      label: 'Paquetes',
                      data: [1, 2, 3, 4, 5].map(s => analyticsData?.paquetes?.porSemana?.[s] || 0),
                      backgroundColor: 'rgba(118, 75, 162, 0.7)',
                      borderColor: '#764ba2',
                      borderWidth: 1,
                      borderRadius: 4,
                    }]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      y: { beginAtZero: true, ticks: { stepSize: 1, precision: 0 } },
                      x: { grid: { display: false } }
                    },
                  }}
                />
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Ranking Vendedores (Admin) o Resumen Personal (Vendedor) */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {isAdmin ? 'Ranking Vendedores' : 'Mi Actividad'}
            </Typography>
            {analyticsCargando ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
              <TableContainer sx={{ maxHeight: 220 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Usuario</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Creados</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(analyticsData?.paquetes?.rankingVendedores || []).map((v: any, i: number) => (
                      <TableRow key={i} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'grey.50' } }}>
                        <TableCell sx={{ py: 0.8 }}>{v.nombre}</TableCell>
                        <TableCell align="right" sx={{ py: 0.8, fontWeight: 600 }}>{v.cantidad}</TableCell>
                      </TableRow>
                    ))}
                    {(!analyticsData?.paquetes?.rankingVendedores?.length) && (
                      <TableRow><TableCell colSpan={2} align="center" sx={{ color: 'text.disabled' }}>Sin datos</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>


        {/* Ganancia Proyectada — USD y ARS separados */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderLeft: '4px solid #43a047', background: 'linear-gradient(135deg, #f1f8e9 0%, #ffffff 100%)' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Ganancia USD
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: (analyticsData?.ganancia?.totalUSD || 0) >= 0 ? '#1b5e20' : 'text.disabled', mb: 0.5 }}>
              {(analyticsData?.ganancia?.totalUSD || 0) >= 0
                ? `U$D ${(analyticsData?.ganancia?.totalUSD || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
                : 'Sin datos'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Proyectada — paquetes creados este mes
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderLeft: '4px solid #1976d2', background: 'linear-gradient(135deg, #e3f2fd 0%, #ffffff 100%)' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Ganancia ARS
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: (analyticsData?.ganancia?.totalARS || 0) >= 0 ? '#0d47a1' : 'text.disabled', mb: 0.5 }}>
              {(analyticsData?.ganancia?.totalARS || 0) >= 0
                ? `$ ${(analyticsData?.ganancia?.totalARS || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
                : 'Sin datos'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Proyectada — paquetes creados este mes
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Controles y Navegación */}
      <Paper sx={{ p: 2, mb: 3, mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          {/* Tabs de estados */}
          <Tabs
            value={pestañaSeleccionada}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: 48 }}
          >
            {estadosTabla.map((estado, index) => (
              <Tab
                key={index}
                label={estado}
                sx={{
                  minHeight: 48,
                  color: pestañaSeleccionada === index ? 'primary.main' : 'text.secondary',
                  fontWeight: pestañaSeleccionada === index ? 'bold' : 'normal'
                }}
              />
            ))}
          </Tabs>

          {/* Botones de acción */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {isAdmin && seleccionados.length > 0 && (
              <Button
                variant="contained"
                size="small"
                color="warning"
                startIcon={<Autorenew />}
                onClick={handleBulkStateChange}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 2,
                  mr: 2
                }}
              >
                Pasar {seleccionados.length} al siguiente estado
              </Button>
            )}
            <Button variant="contained" startIcon={<GetApp />} onClick={handleExport} size="small">
              Exportar
            </Button>
            <Button variant="contained" startIcon={<Print />} onClick={handlePrint} size="small">
              Imprimir
            </Button>
            <IconButton onClick={handleRefresh} disabled={cargando}>
              {cargando ? <CircularProgress size={20} /> : <Refresh />}
            </IconButton>
          </Box>
        </Box>

        {/* Buscador */}
        <Box sx={{ mt: 2 }}>
          <TextField
            size="small"
            placeholder="Buscar en la tabla..."
            value={terminoBusqueda}
            onChange={(e) => setTerminoBusqueda(e.target.value)}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      {/* Tabla de datos */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 160, textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <Checkbox
                      size="small"
                      indeterminate={
                        paquetesFiltrados.some(p => seleccionados.includes(p.id)) &&
                        !paquetesFiltrados.every(p => seleccionados.includes(p.id))
                      }
                      checked={
                        paquetesFiltrados.length > 0 &&
                        paquetesFiltrados.every(p => seleccionados.includes(p.id))
                      }
                      onChange={handleSelectAll}
                    />
                    Acciones
                  </Box>
                </TableCell>
                <TableCell
                  onClick={() => handleSort('numero')}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  # {campoOrdenamiento === 'numero' && (direccionOrdenamiento === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell
                  onClick={() => handleSort('fechaVenta')}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  Fecha de venta {campoOrdenamiento === 'fechaVenta' && (direccionOrdenamiento === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell
                  onClick={() => handleSort('vendedor')}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  Vendedor {campoOrdenamiento === 'vendedor' && (direccionOrdenamiento === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell
                  onClick={() => handleSort('cliente')}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  Cliente {campoOrdenamiento === 'cliente' && (direccionOrdenamiento === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell
                  onClick={() => handleSort('concepto')}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  Concepto {campoOrdenamiento === 'concepto' && (direccionOrdenamiento === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell
                  onClick={() => handleSort('tarifa')}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  Tarifa {campoOrdenamiento === 'tarifa' && (direccionOrdenamiento === 'asc' ? '↑' : '↓')}
                </TableCell>
                {pestañaSeleccionada !== 0 && (
                  <>
                    <TableCell
                      onClick={() => handleSort('costos')}
                      sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                    >
                      Costos {campoOrdenamiento === 'costos' && (direccionOrdenamiento === 'asc' ? '↑' : '↓')}
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort('ganancia')}
                      sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                    >
                      Ganancia {campoOrdenamiento === 'ganancia' && (direccionOrdenamiento === 'asc' ? '↑' : '↓')}
                    </TableCell>
                  </>
                )}
                <TableCell
                  onClick={() => handleSort('saldo')}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  Saldo {campoOrdenamiento === 'saldo' && (direccionOrdenamiento === 'asc' ? '↑' : '↓')}
                </TableCell>
                {pestañaSeleccionada === 2 && (
                  <TableCell sx={{ textAlign: 'center' }}>
                    Pendiente de pago
                  </TableCell>
                )}

              </TableRow>
            </TableHead>
            <TableBody>
              {paquetesFiltrados
                .slice(pagina * filasPorPagina, pagina * filasPorPagina + filasPorPagina)
                .map((pkg) => (
                  <TableRow
                    key={pkg.id}
                    hover
                    sx={{
                      backgroundColor: pkg.listoParaFinalizar && pkg.estado === "Revisión Operativa"
                        ? 'rgba(76, 175, 80, 0.25)'
                        : 'transparent',
                      '&:hover': {
                        backgroundColor: pkg.listoParaFinalizar && pkg.estado === "Revisión Operativa"
                          ? 'rgba(76, 175, 80, 0.35) !important'
                          : undefined
                      }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
                        <Checkbox
                          size="small"
                          sx={{ mr: 1 }}
                          checked={seleccionados.includes(pkg.id)}
                          onChange={() => handleSelectRow(pkg.id)}
                        />
                        <Tooltip title="Ver Ficha">
                          <IconButton
                            size="small"
                            onClick={() => navigate("/paquetes", { state: { tab: 0, openPackageId: pkg.id, returnTab: 2 } })}
                            sx={{ color: 'primary.main' }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={obtenerInfoSiguienteEstado(pkg.estado).tooltipText}>
                          <IconButton
                            size="small"
                            onClick={() => handleStateChange(pkg.id)}
                            disabled={pkg.estado === "Finalizado"}
                            sx={{
                              backgroundColor: pkg.estado === "Finalizado" ? 'grey.400' : '#ff9800',
                              color: 'white',
                              borderRadius: '50%',
                              width: 28,
                              height: 28,
                              '&:hover': {
                                backgroundColor: pkg.estado === "Finalizado" ? 'grey.400' : '#f57c00'
                              },
                              '&:disabled': {
                                backgroundColor: 'grey.400',
                                color: 'white'
                              }
                            }}
                          >
                            <Autorenew fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" sx={{ color: '#d32f2f' }}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>{pkg.numero}</TableCell>
                    <TableCell>{pkg.fechaVenta.toLocaleDateString('es-ES')}</TableCell>
                    <TableCell>{pkg.vendedor}</TableCell>
                    <TableCell>{pkg.cliente}</TableCell>
                    <TableCell>{pkg.concepto}</TableCell>
                    <TableCell>{formatearMoneda(pkg.tarifa, pkg.moneda)}</TableCell>
                    {pestañaSeleccionada !== 0 && (
                      <>
                        <TableCell>{formatearMoneda(pkg.costos, pkg.moneda)}</TableCell>
                        <TableCell>{formatearMoneda(pkg.ganancia, pkg.moneda)}</TableCell>
                      </>
                    )}
                    <TableCell>
                      {Math.abs(pkg.saldo) < 0.01 ? "" : formatearMoneda(pkg.saldo, pkg.moneda)}
                    </TableCell>
                    {pestañaSeleccionada === 2 && (
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Checkbox
                          checked={pkg.pendientePago || false}
                          onChange={() => handlePaymentPendingToggle(pkg.id)}
                          sx={{
                            color: 'warning.main',
                            ml: 2,
                            '&.Mui-checked': {
                              color: 'warning.main',
                            },
                          }}
                        />
                      </TableCell>
                    )}

                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[20, 50, 100]}
          component="div"
          count={paquetesFiltrados.length}
          rowsPerPage={filasPorPagina}
          page={pagina}
          onPageChange={(_, newPage) => setPagina(newPage)}
          onRowsPerPageChange={(event) => {
            setFilasPorPagina(parseInt(event.target.value, 10));
            setPagina(0);
          }}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      </Paper>
    </Box>
  );
};

export default Dashboard;